import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { BazisInputDefaultComponent } from '@bazis/form/components/base/inputs/input-default.component';
import { map, shareReplay, startWith, tap } from 'rxjs/operators';
import { SHARE_REPLAY_SETTINGS } from '@app/configuration.service';
import { combineLatest } from 'rxjs';

@Component({
    selector: 'app-input-new-password',
    templateUrl: './input-new-password.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class AppNewPasswordComponent extends BazisInputDefaultComponent {
    @Input()
    autocomplete = 'new-password';

    @Input() personalData: string[] = [];

    @Input() symbolsRegExp = /[@$!%*#?&.,-><+_()\%~|^\[\]\/\\]/;

    @Input() popularPasswords: string[] = [
        'qwertyui',
        'asdfghjk',
        '12345678',
        'administrator',
        'password',
        'admin123',
        'user1234',
    ];

    @Input() popularSequences: string[] = ['qwer', 'asdf'];

    showPass = false;

    readonly strengthLevels = [
        {
            id: 'low',
            color: 'danger',
            criterias: ['length8'],
        },
        {
            id: 'middle',
            color: 'warning',
            criterias: [
                'length10',
                'differentRegisters',
                'hasSpecialCharactersOrNumbers',
                'notPopular',
                'noSequences',
            ],
        },
        {
            id: 'high',
            color: 'success',
            criterias: [
                'length12',
                'differentRegisters',
                'hasSpecialCharactersAndNumbers',
                'notPopular',
                'noSequences',
            ],
        },
        {
            id: 'extremelyHigh',
            color: 'success-shade',
            criterias: [
                'length14',
                'differentRegisters',
                'hasSpecialCharactersAndNumbers',
                'notPopular',
                'noSequences',
                'personalData',
            ],
        },
    ];

    strengthCriterias: {
        id: string;
        groupId?: string;
        titleKey?: string;
        titleParam?: any;
        checkFunction: Function;
    }[] = [
        {
            id: 'length8',
            groupId: 'length',
            titleParam: { count: 8 },
            titleKey: 'length',
            checkFunction: (value) => {
                return value.length >= 8;
            },
        },
        {
            id: 'length10',
            groupId: 'length',
            titleParam: { count: 10 },
            titleKey: 'length',
            checkFunction: (value) => {
                return value.length >= 10;
            },
        },
        {
            id: 'length12',
            titleParam: { count: 12 },
            groupId: 'length',
            titleKey: 'length',
            checkFunction: (value) => {
                return value.length >= 12;
            },
        },
        {
            id: 'length14',
            titleParam: { count: 14 },
            titleKey: 'length',
            groupId: 'length',
            checkFunction: (value) => {
                return value.length >= 14;
            },
        },
        {
            id: 'personalData',
            checkFunction: (value) => {
                return this.personalData
                    ? this.personalData.filter((data) => value.includes(data)).length === 0
                    : true;
            },
        },
        {
            id: 'differentRegisters',
            checkFunction: (value) => {
                return /[A-Z]/.test(value) && /[a-z]/.test(value);
            },
        },
        {
            id: 'hasSpecialCharactersOrNumbers',
            groupId: 'specialCharactersNumbers',
            checkFunction: (value) => {
                return this.symbolsRegExp.test(value) || /[0-9]/.test(value);
            },
        },
        {
            id: 'hasSpecialCharactersAndNumbers',
            groupId: 'specialCharactersNumbers',
            checkFunction: (value) => {
                return this.symbolsRegExp.test(value) && /[0-9]/.test(value);
            },
        },
        {
            id: 'notPopular',
            checkFunction: (value) => {
                return this.popularPasswords
                    ? this.popularPasswords.filter((data) => value === data).length === 0
                    : true;
            },
        },
        {
            id: 'noSequences',
            checkFunction: this.checkSequences,
        },
    ];

    strengthCriterias$ = this.field.valueChanges.pipe(
        startWith(this.field.value),
        map((v) => this.checkStrengthCriterias()),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    strengthLevel$ = this.strengthCriterias$.pipe(
        map((criterias) => {
            const criteriasMap = criterias.reduce((acc, current) => {
                return {
                    ...acc,
                    [current.id]: current.isValid,
                };
            }, {});
            const levelsAchieved = this.strengthLevels.filter(
                (level) => !level.criterias.find((v) => !criteriasMap[v]),
            );

            return levelsAchieved?.length > 0
                ? levelsAchieved[levelsAchieved.length - 1]
                : this.strengthLevels[0];
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    strengthCriteriasToDisplay$ = combineLatest([
        this.strengthLevel$,
        this.strengthCriterias$,
    ]).pipe(
        map(([level, criterias]) => {
            const currentLevelIndex = this.strengthLevels.findIndex((v) => v.id === level.id);
            const levels =
                currentLevelIndex < this.strengthLevels.length - 1
                    ? [level.id, this.strengthLevels[currentLevelIndex + 1].id]
                    : [level.id];

            let criteriasFromLevels = [];
            levels.forEach((levelItem) => {
                criteriasFromLevels = criteriasFromLevels.concat(
                    this.strengthLevels.find((v) => v.id === levelItem).criterias,
                );
            });

            // console.log(level, criterias, criteriasFromLevels);

            const uniqueCriteriasFromLevels = Array.from(new Set(criteriasFromLevels)).map((v) =>
                criterias.find((crit) => crit.id === v),
            );

            const uniqueByGroup = [];

            const addedGroups = [];

            for (let i = uniqueCriteriasFromLevels.length - 1; i >= 0; i--) {
                if (uniqueCriteriasFromLevels[i].groupId) {
                    if (addedGroups.indexOf(uniqueCriteriasFromLevels[i].groupId) === -1) {
                        addedGroups.push(uniqueCriteriasFromLevels[i].groupId);
                        uniqueByGroup.push(uniqueCriteriasFromLevels[i]);
                    }
                } else {
                    uniqueByGroup.push(uniqueCriteriasFromLevels[i]);
                }
            }

            // console.log(uniqueByGroup);

            return uniqueByGroup.reverse();
        }),
    );

    checkStrengthCriterias() {
        return this.strengthCriterias.map((criteria) => {
            return {
                ...criteria,
                isValid: criteria.checkFunction(this.field.value),
            };
        });
    }

    checkSequences(value) {
        const minSequenceLength = 3;
        if (value.length < minSequenceLength) return true;

        value = value.toLowerCase(); // ignore register/case
        const findRepeatRun = (s: string, minLen: number) => {
            let runStart = 0;

            for (let i = 1; i <= s.length; i++) {
                if (i < s.length && s[i] === s[i - 1]) continue;

                const runLen = i - runStart;
                if (runLen >= minLen) {
                    return s.slice(runStart, i);
                }
                runStart = i;
            }
            return null;
        };

        const findSequentialRun = (s: string, minLen: number, step: 1 | -1) => {
            const isLetter = (c: string) => c >= 'a' && c <= 'z';
            const isDigit = (c: string) => c >= '0' && c <= '9';

            let runStart = 0;
            let runLen = 1;

            for (let i = 1; i <= s.length; i++) {
                if (i < s.length) {
                    const a = s[i - 1];
                    const b = s[i];

                    // We only consider sequences if they are both letters or both digits
                    const sameClass = (isLetter(a) && isLetter(b)) || (isDigit(a) && isDigit(b));

                    if (sameClass) {
                        if (s.charCodeAt(i) - s.charCodeAt(i - 1) === step) {
                            runLen++;
                            continue;
                        }
                    }
                }

                // sequence broke at i
                if (runLen >= minLen) {
                    const slice = s.slice(runStart, runStart + runLen);
                    return slice;
                }
                runStart = i;
                runLen = 1;
            }
            return null;
        };

        const rep = findRepeatRun(value, minSequenceLength);
        if (rep) return false;

        const asc = findSequentialRun(value, minSequenceLength, +1);
        if (asc) return false;

        const desc = findSequentialRun(value, minSequenceLength, -1);
        if (desc) return false;

        return true;
    }
}
